import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { SofaPanel, SofaButton } from '@sofology/sofologycomponents';

import './Payment.scss';
import { generateDispatchToPropsMap, analyticsHelper, externalScriptHelper } from 'helpers';
import { AppState } from 'store';
import { removeCardPaymentErrorMessage, RemoveCardPaymentErrorMessageFunc } from 'store/payment/actions';
import { BasketErrorModel } from 'models/Errors';
import PaypalPayment from './PaypalPayment/PaypalPayment';
import {
    removePaypalPaymentErrorMessage,
    RemovePaypalPaymentErrorMessageFunc
} from 'store/payment/actions/paypal';
import PaymentLoader from './PaymentLoader/PaymentLoader';
import CardPayment from './CardPayment/CardPayment';

const SHOW_EXTERNAL_ERROR_TIME = 30000;

enum PaymentMethods {
    None,
    Card,
    Paypal
}

interface OwnProps {
}

interface StateProps {
    isCardPaymentProcessing: boolean;
    cardPaymentError: BasketErrorModel | null;
    isPaypalPaymentProcessing: boolean;
    paypalPaymentError: BasketErrorModel | null;
    isPaypalPaymentCompleteProcessing: boolean;
}

interface DispatchProps {
    removeCardPaymentErrorMessage: RemoveCardPaymentErrorMessageFunc;
    removePaypalPaymentErrorMessage: RemovePaypalPaymentErrorMessageFunc;
}

interface OwnState {
    selectedPaymentMethod: PaymentMethods;
}

class Payment extends Component<OwnProps & StateProps & DispatchProps, OwnState> {
    private selectorElement = React.createRef<HTMLDivElement>();

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        this.state = {
            selectedPaymentMethod: this.props.isPaypalPaymentProcessing ? PaymentMethods.Paypal : PaymentMethods.None
        };
    }

    componentDidUpdate(prevProps: OwnProps & StateProps & DispatchProps) {
        if (!prevProps.cardPaymentError && this.props.cardPaymentError) {
            this.setState({ selectedPaymentMethod: PaymentMethods.None });
            this.clearExternalErrorAfterTimeout();
        }

        if (!prevProps.paypalPaymentError && this.props.paypalPaymentError) {
            this.setState({ selectedPaymentMethod: PaymentMethods.None });
            this.clearExternalErrorAfterTimeout();
        }
    }

    handleSelectorClick = (paymentType: PaymentMethods.Card | PaymentMethods.Paypal): any => async (): Promise<void> => {
        // ignore paypal toggling until external script loaded
        if (paymentType === PaymentMethods.Paypal && !externalScriptHelper.getExternalScript<any>('paypal')) {
            return;
        }

        analyticsHelper.sendToGooglePaymentMethodChange(paymentType === PaymentMethods.Card ? 'card' : 'paypal');

        this.setState({ selectedPaymentMethod: paymentType });
    };

    render(): JSX.Element {
        const { isPaypalPaymentCompleteProcessing } = this.props;
        const { selectedPaymentMethod } = this.state;

        if (isPaypalPaymentCompleteProcessing) {
            return (
                <div className='payment'>
                    <PaymentLoader message={'Awaiting response from PayPal'} />
                </div>
            );
        }

        return (
            <div className='payment'>
                <SofaPanel>
                    {this.renderPaymentError()}
                    {this.renderPaymentSelector()}
                    {/* need to hide component by CSS instead of disable rendering - because card payment component has long initialization */}
                    <div className={selectedPaymentMethod === PaymentMethods.Card ? 'payment__card' : 'payment__card--hidden'}>
                        <CardPayment />
                    </div>
                    <div className={selectedPaymentMethod === PaymentMethods.Paypal ? 'payment__paypal' : 'payment__paypal--hidden'}>
                        <PaypalPayment />
                    </div>
                </SofaPanel>
            </div>
        );
    }

    renderPaymentError(): JSX.Element | null {
        return (
            <div className='payment__payment-error'>
                <div>{this.props.cardPaymentError ? this.props.cardPaymentError.message : null}</div>
                <div>{this.props.paypalPaymentError?.message ? this.props.paypalPaymentError?.message : null}</div>
            </div>
        );
    }

    renderPaymentSelector(): JSX.Element {
        const { selectedPaymentMethod } = this.state;
        const { isPaypalPaymentProcessing, isCardPaymentProcessing } = this.props;

        return (
            <div className='payment__selector' ref={this.selectorElement}>
                <div className='payment__selector-title'>
                    Please select a payment method
                </div>
                <div className='payment__selector-items'>
                    {this.renderPaymentSelectorItem('Credit/Debit Card', selectedPaymentMethod === PaymentMethods.Card, isPaypalPaymentProcessing, PaymentMethods.Card)}
                    {this.renderPaymentSelectorItem('PayPal', selectedPaymentMethod === PaymentMethods.Paypal, isCardPaymentProcessing, PaymentMethods.Paypal)}
                </div>
            </div>
        );
    }

    renderPaymentSelectorItem(title: string, isActive: boolean, isDisabled: boolean, paymentType: PaymentMethods.Card | PaymentMethods.Paypal): JSX.Element {
        const itemImages = (paymentType === PaymentMethods.Card)
            ? (<>
                <div className='payment__selector-item-image payment__selector-item-image--visa'></div>
                <div className='payment__selector-item-image payment__selector-item-image--mastercard'></div>
                <div className='payment__selector-item-image payment__selector-item-image--maestro'></div>
                <div className='payment__selector-item-image payment__selector-item-image--american'></div>
            </>)
            : (<><div className='payment__selector-item-image payment__selector-item-image--paypal'></div></>);

        const paymentButtonText =
            <div className={'payment__selector-item'}>
                <div className='payment__selector-item-text'>{title}</div>
                <div className='payment__selector-item-images'>{itemImages}</div>
            </div>;

        return (
            <div className='payment__selector-container'>
                <SofaButton
                    text={paymentButtonText}
                    variant='choice'
                    disabled={isDisabled}
                    active={isActive}
                    onClick={!isDisabled ? this.handleSelectorClick(paymentType) : undefined}
                >
                </SofaButton>
            </div>
        );
    }

    clearExternalErrorAfterTimeout(): void {
        setTimeout(() => {
            this.props.removeCardPaymentErrorMessage();
            this.props.removePaypalPaymentErrorMessage();
        }, SHOW_EXTERNAL_ERROR_TIME);
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        isCardPaymentProcessing: state.paymentState.isCardPaymentProcessing,
        cardPaymentError: state.paymentState.cardPaymentError,
        isPaypalPaymentProcessing: state.paymentState.isPaypalPaymentProcessing,
        paypalPaymentError: state.paymentState.paypalPaymentError,
        isPaypalPaymentCompleteProcessing: state.paymentState.isPaypalPaymentCompleteProcessing,
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(
        dispatch,
        [
            removeCardPaymentErrorMessage,
            removePaypalPaymentErrorMessage
        ]
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(Payment);